/**
 * LeatherRoundController — the Round (corner-fillet) sub-tool for leather
 * outlines (#974, epic #781): drag the handle at a part's or cutout's corner to
 * inscribe an arc there, and drag it back past the corner to make it sharp
 * again.
 *
 * A tool OWNS a controller instance and DRIVES it by composition (it is NOT a
 * base class) — the arrangement `RoundController` / `LeatherMirrorController` /
 * `OutlineOffsetController` already use.
 *
 * Nothing geometric is reimplemented. The corner arithmetic
 * (`getCornerGeometry` / `radiusFromHandlePosition` / `applyCornerRounding`) is
 * `constructionRounding.ts`, already pathData-based; the handle placement and
 * pickup rules are `cornerHandles.ts`, extracted from `RoundController` for
 * this ticket so both tools resolve a click to the same corner (#990).
 *
 * Two differences from the construction sub-tool, both deliberate:
 *
 *  1. HANDLES ARE DRAWN BY THE CONTROLLER, as ephemeral overlay dots, rather
 *     than by the Scene. Scene's handle renderer is wired into the
 *     construction-group's per-line subgroups; leather outlines live elsewhere
 *     in the tree, and an overlay the tool owns is both simpler and cleaned up
 *     automatically when the tool deactivates.
 *
 *  2. LINKED OUTLINES REFUSE. A part traced from construction geometry
 *     re-derives its outline on every commit, so rounding stored against it
 *     would be rebuilt away — round the construction line instead. Same rule
 *     the Edit-points method already applies.
 */
import paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { GuideRay, Scene } from "../canvas/Scene";
import { isVisible, type Project } from "../model/Project";
import {
  applyCornerRounding,
  arcCenterDistance,
  enumerateRoundableCorners,
  getCornerGeometry,
  radiusFromHandlePosition,
  type CornerGeometry,
} from "../model/constructionRounding";
import {
  cornerHandlePoint,
  displayRadiusFor,
  hitCornerHandle,
  mergeCornerRadius,
  ROUND_MIN_RADIUS_MM,
  type RoundableOutline,
} from "../model/cornerHandles";
import { pieceDisplayOffset, translatePathData } from "../model/pieceTransform";
import type { ToolSettings } from "./EditorContext";
import { mapPiece } from "../model/pieceDuplicate";
import { pickLeatherObjectAt, selectionForPick } from "./leatherPick";
import { cssVar } from "../colors";

/** Accent pair for anything this tool draws. A corner handle sits INSIDE its
 *  outline and the rounded preview traces it, so both usually land on leather —
 *  where the light accent washes out against `--leather-fill`. The verdict is
 *  taken per mark from `Scene.isPointOverLeather`, never assumed from the tool,
 *  so a cutout's handle in Leather mode (a real void, mat showing through)
 *  correctly takes the light one. */
const ACCENT = () => cssVar("--canvas-accent");
const ACCENT_OVER_LEATHER = () => cssVar("--canvas-accent-leather");

/** Handle dot radius in screen px (drawn unscaled, so it stays grabbable at
 *  any zoom). */
const HANDLE_DOT_PX = 4.5;

export interface LeatherRoundHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  getSettings(): ToolSettings;
  commit(label: string, mutate: (proj: Project) => Project): void;
  setSelection(sel: Parameters<Scene["setSelection"]>[0]): void;
}

/** Which leather outline a corner belongs to, recovered from the opaque key
 *  the shared scan carries through. */
type OutlineRef =
  | { kind: "piece"; pieceId: string }
  | { kind: "cutout"; pieceId: string; cutoutId: string };

function parseKey(key: string): OutlineRef | null {
  const [kind, id, cutId] = key.split(":");
  if (kind === "piece" && id) return { kind: "piece", pieceId: id };
  if (kind === "cutout" && id && cutId) {
    return { kind: "cutout", pieceId: id, cutoutId: cutId };
  }
  return null;
}

/**
 * Every leather outline whose corners can be rounded, in DISPLAY space.
 *
 * Construction-LINKED parts and cutouts are excluded: their geometry
 * re-derives from the lines on every commit, so rounding stored here would be
 * rebuilt away on the next pass — the same reason Edit-points refuses them.
 *
 * `sharpPathData` is the rounding metadata's source when there is one, so a
 * corner keeps its identity and its position after being rounded.
 */
export function roundableLeatherOutlines(
  project: Project,
  partLayoutView: "spread" | "assembled",
  scope: paper.PaperScope,
  preferredPieceId?: string | null,
): RoundableOutline[] {
  const out: RoundableOutline[] = [];
  for (const piece of project.pieces) {
    if (!isVisible(piece)) continue;
    const off = pieceDisplayOffset(piece, partLayoutView);
    const shift = (d: string) =>
      off.dx || off.dy ? translatePathData(d, off.dx, off.dy, scope) : d;
    if (!piece.constructionOutline) {
      out.push({
        key: `piece:${piece.id}`,
        sharpPathData: shift(
          piece.cornerRounding?.sharpPathData ?? piece.shapePathData,
        ),
        corners: piece.cornerRounding?.corners ?? [],
        preferred: preferredPieceId === piece.id,
      });
    }
    for (const cut of piece.cutouts ?? []) {
      if (!isVisible(cut) || cut.constructionOutline) continue;
      out.push({
        key: `cutout:${piece.id}:${cut.id}`,
        sharpPathData: shift(cut.cornerRounding?.sharpPathData ?? cut.pathData),
        corners: cut.cornerRounding?.corners ?? [],
        preferred: preferredPieceId === piece.id,
      });
    }
  }
  return out;
}

export class LeatherRoundController {
  /** In-flight corner drag. Mirrors `RoundController.roundCornerDrag`: the
   *  handle sits at the centre of the arc the current radius would inscribe,
   *  so the drag reads as "pull the arc centre away from the corner". */
  private drag: {
    ref: OutlineRef;
    key: string;
    pathIndex: number;
    segmentIndex: number;
    geom: CornerGeometry;
    /** Cursor offset from the dot at press, so a click that grazed the dot's
     *  edge doesn't snap it under the cursor. */
    grabOffset: { x: number; y: number };
    radius: number;
    preview: paper.Item | null;
  } | null = null;

  /** The handle dots + any hover highlight. Ephemeral overlay items. */
  private handles: paper.Item[] = [];

  private presetRadiusMm: number | null = null;

  /** Modifier state snapshotted at the last pointer event. Shift FORCES
   *  all-corners for that gesture regardless of the panel toggle, matching the
   *  construction sub-tool (`effectiveRoundAll`). */
  private shiftHeldAtEvent = false;
  private liveRadiusCallback?: (mm: number) => void;

  /** The corner most recently grabbed, so a later panel change can act on the
   *  corner the user was working on rather than on the whole outline. Cleared
   *  when the selection moves to a different object. */
  private lastCorner: {
    key: string;
    pathIndex: number;
    segmentIndex: number;
  } | null = null;

  constructor(private host: LeatherRoundHost) {}

  /** Round every eligible corner of the outline, not just the grabbed one:
   *  the "All corners" snap-panel toggle, or Shift held for this gesture.
   *  Most rounded leather shapes want symmetrical corners, which is why the
   *  toggle defaults on. */
  private effectiveRoundAll(): boolean {
    return this.host.getSettings().roundAllCorners || this.shiftHeldAtEvent;
  }

  /** Every corner an all-corners commit / preview should touch on `outline`,
   *  each clamped to what that corner can actually take — a tight corner
   *  accepts a smaller radius than a wide one, and clamping per corner is what
   *  keeps the sweep uniform where it can be and valid where it can't. */
  private allCornerEntries(
    outline: RoundableOutline,
    radius: number,
  ): Array<{ pathIndex: number; segmentIndex: number; radius: number }> {
    const scope = this.host.getScope();
    const out: Array<{
      pathIndex: number;
      segmentIndex: number;
      radius: number;
    }> = [];
    for (const c of enumerateRoundableCorners(
      outline.sharpPathData,
      scope,
      ROUND_MIN_RADIUS_MM,
    )) {
      const geom = getCornerGeometry(
        outline.sharpPathData,
        c.pathIndex,
        c.segmentIndex,
        scope,
      );
      if (!geom) continue;
      out.push({ ...c, radius: Math.min(radius, geom.maxRadius) });
    }
    return out;
  }

  /**
   * The panel's Radius value — typed, or picked from the preset chips.
   *
   * Two jobs, because the field is BOTH a property of the current selection
   * and an arming value for the next click:
   *
   *  1. If a part or cutout is selected, the radius is applied to it right
   *     away. A number in a panel field is expected to act on what's selected;
   *     making the user pick a size and then also go hunt a corner reads as the
   *     control not working.
   *  2. It stays armed regardless, so a click on any OTHER corner commits at
   *     the same radius without a drag — the construction contract.
   *
   * Applying to a selection always covers EVERY eligible corner of that
   * outline, whatever the All-corners toggle says: the toggle disambiguates a
   * gesture aimed at one corner, while this value is aimed at the whole object.
   */
  setPresetRadius(mm: number | null): void {
    this.presetRadiusMm = mm;
    if (mm !== null && isFinite(mm) && mm >= ROUND_MIN_RADIUS_MM) {
      this.applyToSelection(mm);
    }
    this.refreshHandles();
  }

  /** The selected part / cutout as a roundable outline, or null when nothing
   *  roundable is selected (including a linked outline, which refuses). */
  private selectedOutline(): { ref: OutlineRef; key: string } | null {
    const sel = this.host.getScene().getSelection();
    if (!sel.pieceId) return null;
    const key = sel.cutoutId
      ? `cutout:${sel.pieceId}:${sel.cutoutId}`
      : `piece:${sel.pieceId}`;
    if (!this.outlines().some((o) => o.key === key)) return null;
    const ref = parseKey(key);
    return ref ? { ref, key } : null;
  }

  /**
   * The selection changed under the tool — from a canvas click or the Parts
   * list. Round stays open and follows it: the handles move to the new object,
   * and a radius already set in the panel applies to it immediately, so picking
   * part after part rounds each one without re-typing anything.
   */
  onSelectionChanged(): void {
    const target = this.selectedOutline();
    if (!target || this.lastCorner?.key !== target.key) this.lastCorner = null;
    const mm = this.presetRadiusMm;
    // Only apply on a selection change in ALL-CORNERS mode. There, picking an
    // object is a complete instruction — "these corners, that radius" — so
    // acting on it is what the user asked for. In single-corner mode it isn't:
    // the user means one corner at a time, and no corner has been named yet, so
    // rounding the whole outline would be the tool doing something they never
    // said. The radius stays armed for their next corner click either way.
    if (
      this.host.getSettings().roundAllCorners &&
      mm !== null &&
      isFinite(mm) &&
      mm >= ROUND_MIN_RADIUS_MM
    ) {
      this.applyToSelection(mm);
    }
    this.refreshHandles();
  }

  /**
   * Pick the object under the cursor. A cutout selects as itself; a seam or a
   * bare part hit selects the part, since a seam has no corners of its own.
   * Returns true when something was picked, so the caller can report the press
   * as handled.
   */
  private selectAt(p: { x: number; y: number }): boolean {
    const pick = pickLeatherObjectAt(
      this.host.getScene(),
      this.host.getProject(),
      this.host.getScope(),
      p,
      { includeStitch: false },
    );
    if (!pick) {
      // Bare mat: drop the selection, so a radius already set arms the NEXT
      // corner click instead of continuing to act on whatever was selected.
      // That's the way out of "this value belongs to that part".
      this.host.setSelection({ pieceId: null, decorationId: null });
      return true;
    }
    this.host.setSelection(selectionForPick(pick));
    return true;
  }

  /**
   * Apply a panel radius to what the user is working on.
   *
   * With All corners on (the default) that's every corner of the selected
   * outline. With it off, a panel change acts on the corner most recently
   * GRABBED — "change the one I'm working on" is the only sensible reading of
   * a single-corner mode, and it lets the field work as a numeric entry for a
   * corner you just dragged approximately.
   *
   * Shift isn't consulted here: it's a drag modifier, and nobody holds it while
   * clicking a preset chip.
   */
  private applyToSelection(mm: number): void {
    const target = this.selectedOutline();
    if (!target) return;
    if (!this.host.getSettings().roundAllCorners) {
      // Single-corner mode acts on the corner most recently GRABBED, and on
      // nothing at all when none has been. Falling back to "all corners" here
      // would round the whole outline in the one mode whose entire point is
      // that it doesn't.
      const last = this.lastCorner;
      if (!last || last.key !== target.key) return;
      this.commitRadius(target.ref, last.pathIndex, last.segmentIndex, mm);
      return;
    }
    this.commitAllCorners(target.ref, target.key, mm);
  }

  /** Per-frame bridge to the panel's Radius input, so the value can be read
   *  while sweeping. Silent while a preset is locked — the field already shows
   *  the number the commit will use, and overwriting it mid-drag would fight
   *  the user's typing. */
  setLiveRadiusCallback(fn: ((mm: number) => void) | undefined): void {
    this.liveRadiusCallback = fn;
  }

  isDragActive(): boolean {
    return this.drag !== null;
  }

  /** Test hook (#974): every corner handle's position in project mm, so e2e
   *  can aim at a handle where it actually sits rather than re-deriving the
   *  rest offset. */
  getHandlePositions(): Array<{
    key: string;
    pathIndex: number;
    segmentIndex: number;
    x: number;
    y: number;
  }> {
    const out: Array<{
      key: string;
      pathIndex: number;
      segmentIndex: number;
      x: number;
      y: number;
    }> = [];
    for (const outline of this.outlines()) {
      for (const c of this.cornersOf(outline)) {
        out.push({
          key: outline.key,
          pathIndex: c.pathIndex,
          segmentIndex: c.segmentIndex,
          x: c.point.x,
          y: c.point.y,
        });
      }
    }
    return out;
  }

  /** Test hook (#974): the corner currently under the cursor as
   *  `"<key>#<pathIndex>:<segmentIndex>"`, or null. */
  getHoverCorner(): string | null {
    if (!this.drag) return null;
    return `${this.drag.key}#${this.drag.pathIndex}:${this.drag.segmentIndex}`;
  }

  // -------------------------------------------------------------------
  // Handles
  // -------------------------------------------------------------------

  /** Redraw a dot on every roundable corner. Called on activate and after any
   *  commit, so the dots track the geometry they belong to. */
  refreshHandles(): void {
    this.clearHandles();
    const scope = this.host.getScope();
    const scene = this.host.getScene();
    const zoom = scope.view.zoom > 0 ? scope.view.zoom : 1;
    for (const outline of this.outlines()) {
      for (const corner of this.cornersOf(outline)) {
        const dot = new scope.Path.Circle(
          new scope.Point(corner.point.x, corner.point.y),
          HANDLE_DOT_PX / zoom,
        );
        dot.fillColor = new scope.Color(this.accentAt(corner.point));
        dot.strokeColor = null;
        dot.data = { ephemeral: true };
        this.handles.push(dot);
      }
    }
    void scene;
  }

  private cornersOf(outline: RoundableOutline): Array<{
    pathIndex: number;
    segmentIndex: number;
    point: { x: number; y: number };
  }> {
    const scope = this.host.getScope();
    const out: Array<{
      pathIndex: number;
      segmentIndex: number;
      point: { x: number; y: number };
    }> = [];
    let cp: paper.CompoundPath;
    try {
      cp = new scope.CompoundPath({
        pathData: outline.sharpPathData,
        insert: false,
      });
    } catch {
      return out;
    }
    const subs: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];
    const radiusByKey = new Map<string, number>();
    for (const c of outline.corners) {
      radiusByKey.set(`${c.pathIndex}:${c.segmentIndex}`, c.radius);
    }
    for (let pi = 0; pi < subs.length; pi++) {
      const sub = subs[pi]!;
      for (let si = 0; si < sub.segments.length; si++) {
        const geom = getCornerGeometry(outline.sharpPathData, pi, si, scope);
        if (!geom) continue;
        if (geom.maxRadius < ROUND_MIN_RADIUS_MM) continue;
        // A live drag wins over the committed radius so the dots travel WITH
        // the cursor instead of sitting at their old positions until release —
        // and with all-corners on, every dot the sweep covers moves together,
        // which is the only way the gesture reads as one action.
        const live = this.liveRadiusFor(outline.key, pi, si, geom);
        out.push({
          pathIndex: pi,
          segmentIndex: si,
          point: cornerHandlePoint(
            geom,
            live ??
              displayRadiusFor(
                geom,
                radiusByKey.get(`${pi}:${si}`),
                this.presetRadiusMm,
              ),
          ),
        });
      }
    }
    cp.remove();
    return out;
  }

  /** The radius an in-flight drag implies for one corner, or null when no drag
   *  touches it. Clamped per corner, matching what the commit will store. */
  private liveRadiusFor(
    outlineKey: string,
    pathIndex: number,
    segmentIndex: number,
    geom: CornerGeometry,
  ): number | null {
    const drag = this.drag;
    if (!drag || drag.key !== outlineKey) return null;
    const isDragged =
      drag.pathIndex === pathIndex && drag.segmentIndex === segmentIndex;
    if (!isDragged && !this.effectiveRoundAll()) return null;
    return Math.min(drag.radius, geom.maxRadius);
  }

  private outlines(): RoundableOutline[] {
    const scene = this.host.getScene();
    return roundableLeatherOutlines(
      this.host.getProject(),
      scene.getPartLayoutView(),
      this.host.getScope(),
      scene.getSelection().pieceId ?? null,
    );
  }

  private clearHandles(): void {
    for (const h of this.handles) h.remove();
    this.handles = [];
  }

  /**
   * Radius readout rays — one per corner being rounded, so an all-corners
   * sweep shows every corner it is about to change rather than just the
   * grabbed one. Each ray runs from the arc centre out to the arc, labelled
   * with that corner's effective radius.
   *
   * Each ray carries its OWN over-leather verdict (an all-corners sweep can
   * span corners that sit over leather and over a void), and the probe is the
   * ray's midpoint — its endpoints sit on the outline, where the question has
   * no answer. See the overlays-over-leather rule in AGENTS.md.
   */
  private refreshRays(
    outline: RoundableOutline,
    applyToAll: boolean,
    drag: { geom: CornerGeometry; radius: number },
  ): void {
    // No gate needed here: `Scene.setGuideRays` already drops everything when
    // rays are switched off for the mode (`raysLeather` defaults OFF, #745).
    const scene = this.host.getScene();
    const scope = this.host.getScope();
    const rays: GuideRay[] = [];
    const addRay = (geom: CornerGeometry, r: number) => {
      // Same guards construction uses: nothing to show at zero radius, and a
      // near-straight "corner" has no meaningful bisector to draw along.
      if (r <= 0 || Math.sin(geom.halfAngle) <= 1e-3) return;
      const centreDist = arcCenterDistance(geom, r);
      const from = {
        x: geom.cornerPoint.x + geom.bisectorDir.x * centreDist,
        y: geom.cornerPoint.y + geom.bisectorDir.y * centreDist,
      };
      const to = {
        x: from.x - geom.bisectorDir.x * r,
        y: from.y - geom.bisectorDir.y * r,
      };
      rays.push({
        from,
        to,
        radiusMm: r,
        overLeather: scene.isPointOverLeather({
          x: (from.x + to.x) / 2,
          y: (from.y + to.y) / 2,
        }),
      });
    };
    if (applyToAll) {
      for (const c of this.allCornerEntries(outline, drag.radius)) {
        const geom = getCornerGeometry(
          outline.sharpPathData,
          c.pathIndex,
          c.segmentIndex,
          scope,
        );
        if (geom) addRay(geom, c.radius);
      }
    } else {
      addRay(drag.geom, drag.radius);
    }
    scene.setGuideRays(rays);
  }

  /** The accent to draw a mark at `pt` in — dark over leather, light over the
   *  bare mat. See the "overlays drawn over leather" rule in AGENTS.md. */
  private accentAt(pt: { x: number; y: number }): string {
    return this.host.getScene().isPointOverLeather(pt)
      ? ACCENT_OVER_LEATHER()
      : ACCENT();
  }

  // -------------------------------------------------------------------
  // Pointer flow
  // -------------------------------------------------------------------

  handleDown(p: { x: number; y: number }, s?: PointerSample): boolean {
    this.shiftHeldAtEvent = !!s?.shiftKey;
    const scope = this.host.getScope();
    const hit = hitCornerHandle(this.outlines(), p, scope, {
      presetRadiusMm: this.presetRadiusMm,
    });
    // No handle under the press: treat it as picking what to work on next
    // rather than as nothing. Handles sit within a few px of a corner, so this
    // can't shadow the rounding gesture — anywhere else on a part is
    // unambiguously "select me", and the tool stays open either way.
    if (!hit) return this.selectAt(p);
    const ref = parseKey(hit.key);
    if (!ref) return false;
    this.cancel();
    this.drag = {
      ref,
      key: hit.key,
      pathIndex: hit.pathIndex,
      segmentIndex: hit.segmentIndex,
      geom: hit.geom,
      grabOffset: {
        x: hit.handlePoint.x - p.x,
        y: hit.handlePoint.y - p.y,
      },
      // Seed from what's committed; the preset only enters at commit time,
      // where one too big for the corner is skipped rather than clamped.
      radius: Math.min(hit.existingRadius ?? 0, hit.geom.maxRadius),
      preview: null,
    };
    // Report the grabbed corner's current radius so the panel's field shows
    // what you just took hold of. `setLive` is display-only and defers to a
    // stored preset, so this can't stomp a value the user typed.
    this.lastCorner = {
      key: hit.key,
      pathIndex: hit.pathIndex,
      segmentIndex: hit.segmentIndex,
    };
    this.liveRadiusCallback?.(hit.existingRadius ?? 0);
    this.refreshPreview(p);
    return true;
  }

  handleMove(p: { x: number; y: number }, s?: PointerSample): boolean {
    if (!this.drag) return false;
    this.shiftHeldAtEvent = !!s?.shiftKey;
    this.refreshPreview(p);
    return true;
  }

  handleUp(): boolean {
    const drag = this.drag;
    if (!drag) return false;
    if (drag.preview) drag.preview.remove();
    this.drag = null;

    if (drag.radius < ROUND_MIN_RADIUS_MM) {
      // Pulled back past the corner: un-round it if it was rounded, otherwise
      // treat a bare click as "apply the preset" — but only when the preset
      // actually fits, since the user asked for a specific dimension and
      // silently substituting a smaller one would be worse than doing nothing.
      const wasRounded = this.storedRadius(drag.ref, drag) !== undefined;
      if (wasRounded && this.effectiveRoundAll()) {
        // Symmetry with the sweep: if one drag rounded every corner, one drag
        // back has to sharpen every corner. Un-rounding just the grabbed one
        // would leave the rest rounded and demand a separate gesture each —
        // construction un-rounds singly even in all-corners mode, which is the
        // asymmetry this deliberately doesn't copy.
        this.commitSharpenAll(drag.ref);
      } else if (wasRounded) {
        this.commitRadius(drag.ref, drag.pathIndex, drag.segmentIndex, 0);
      } else {
        const preset = this.presetRadiusMm;
        if (
          preset !== null &&
          isFinite(preset) &&
          preset > 0 &&
          preset <= drag.geom.maxRadius
        ) {
          this.commitRadius(
            drag.ref,
            drag.pathIndex,
            drag.segmentIndex,
            preset,
          );
        }
      }
    } else if (this.effectiveRoundAll()) {
      this.commitAllCorners(drag.ref, drag.key, drag.radius);
    } else {
      this.commitRadius(
        drag.ref,
        drag.pathIndex,
        drag.segmentIndex,
        drag.radius,
      );
    }
    this.host.getScene().clearGuideRays();
    this.refreshHandles();
    return true;
  }

  cancel(): void {
    if (this.drag?.preview) this.drag.preview.remove();
    this.drag = null;
    this.host.getScene().clearGuideRays();
  }

  /** Drop everything this controller drew. */
  clear(): void {
    this.cancel();
    this.clearHandles();
  }

  // -------------------------------------------------------------------
  // Preview
  // -------------------------------------------------------------------

  private refreshPreview(cursor: { x: number; y: number }): void {
    const drag = this.drag;
    if (!drag) return;
    const scope = this.host.getScope();
    if (drag.preview) {
      drag.preview.remove();
      drag.preview = null;
    }
    const handlePos = {
      x: cursor.x + drag.grabOffset.x,
      y: cursor.y + drag.grabOffset.y,
    };
    let radius = radiusFromHandlePosition(drag.geom, handlePos);
    const preset = this.presetRadiusMm;
    if (preset !== null && isFinite(preset) && preset > 0) {
      // A locked preset OVERRIDES the cursor entirely, exactly as the
      // construction sub-tool does: once you've named a size, grabbing a
      // handle is how you apply that size to a corner, not an invitation to
      // dial in a different one by hand. Without this the drag quietly won and
      // the number you picked was ignored.
      //
      // A preset too big for this corner shows radius 0 rather than clamping
      // down — the user asked for a specific dimension and we don't silently
      // substitute another; `handleUp` skips the commit for the same reason, so
      // the preview matches what a release would do.
      radius = preset <= drag.geom.maxRadius ? preset : 0;
    } else if (radius > drag.geom.maxRadius) {
      radius = drag.geom.maxRadius;
    }
    drag.radius = radius;
    const outline = this.outlines().find((o) => o.key === drag.key);
    if (!outline) return;
    const applyToAll = this.effectiveRoundAll();
    // All-corners previews the WHOLE sweep, so what the release commits is
    // what was on screen rather than one corner standing in for the rest.
    const corners = applyToAll
      ? this.allCornerEntries(outline, drag.radius)
      : mergeCornerRadius(
          outline.corners,
          drag.pathIndex,
          drag.segmentIndex,
          drag.radius,
        );
    if (this.presetRadiusMm === null) this.liveRadiusCallback?.(drag.radius);
    this.refreshRays(outline, applyToAll, drag);
    // Re-place the dots for this frame's radius. Cheap (a handful of small
    // circles) and it's what makes the handle feel grabbed rather than
    // watched.
    this.refreshHandles();
    const d = applyCornerRounding(outline.sharpPathData, corners, scope);
    if (!d) return;
    try {
      const item = new scope.CompoundPath({ pathData: d, insert: true });
      // Probe the handle position rather than a point on the outline itself:
      // the arc runs along the boundary, where "over leather?" has no answer.
      item.strokeColor = new scope.Color(this.accentAt(handlePos));
      item.strokeWidth = 1.6;
      item.strokeScaling = false;
      item.dashArray = [4, 3];
      item.fillColor = null;
      item.data = { ephemeral: true };
      drag.preview = item;
    } catch {
      /* preview only */
    }
  }

  // -------------------------------------------------------------------
  // Commit
  // -------------------------------------------------------------------

  /** The radius currently stored for a corner, or undefined. */
  private storedRadius(
    ref: OutlineRef,
    at: { pathIndex: number; segmentIndex: number },
  ): number | undefined {
    const project = this.host.getProject();
    const piece = project.pieces.find((p) => p.id === ref.pieceId);
    if (!piece) return undefined;
    const rounding =
      ref.kind === "piece"
        ? piece.cornerRounding
        : (piece.cutouts ?? []).find((c) => c.id === ref.cutoutId)
            ?.cornerRounding;
    return rounding?.corners.find(
      (c) => c.pathIndex === at.pathIndex && c.segmentIndex === at.segmentIndex,
    )?.radius;
  }

  /** Drop every rounding on the outline and restore its sharp geometry — the
   *  all-corners counterpart of dragging one handle back past its corner. */
  private commitSharpenAll(ref: OutlineRef): void {
    this.host.commit("Sharpen corners", (proj) =>
      mapPiece(proj, ref.pieceId, (piece) => {
        if (ref.kind === "piece") {
          const sharp = piece.cornerRounding?.sharpPathData;
          if (!sharp) return piece;
          const { cornerRounding: _drop, ...rest } = piece;
          return { ...rest, shapePathData: sharp };
        }
        return {
          ...piece,
          cutouts: (piece.cutouts ?? []).map((cut) => {
            if (cut.id !== ref.cutoutId) return cut;
            const sharp = cut.cornerRounding?.sharpPathData;
            if (!sharp) return cut;
            const { cornerRounding: _drop, ...rest } = cut;
            return { ...rest, pathData: sharp };
          }),
        };
      }),
    );
  }

  /**
   * Apply one radius to EVERY eligible corner of the outline in a single
   * commit — the "All corners" toggle's whole point, since most rounded
   * leather shapes (a wallet back, a luggage tag, a key fob) want symmetrical
   * corners and rounding them one at a time is four gestures and four undo
   * steps.
   *
   * Each corner is clamped to its own `maxRadius`, so a shape with one tight
   * corner still rounds uniformly everywhere it can rather than refusing the
   * whole sweep.
   */
  private commitAllCorners(ref: OutlineRef, key: string, radius: number): void {
    const outline = this.outlines().find((o) => o.key === key);
    if (!outline) return;
    const entries = this.allCornerEntries(outline, radius).filter(
      (e) => e.radius >= ROUND_MIN_RADIUS_MM,
    );
    if (entries.length === 0) return;
    this.host.commit("Round corners", (proj) =>
      mapPiece(proj, ref.pieceId, (piece) => {
        if (ref.kind === "piece") {
          return {
            ...piece,
            cornerRounding: {
              sharpPathData:
                piece.cornerRounding?.sharpPathData ?? piece.shapePathData,
              corners: entries,
            },
          };
        }
        return {
          ...piece,
          cutouts: (piece.cutouts ?? []).map((cut) =>
            cut.id === ref.cutoutId
              ? {
                  ...cut,
                  cornerRounding: {
                    sharpPathData:
                      cut.cornerRounding?.sharpPathData ?? cut.pathData,
                    corners: entries,
                  },
                }
              : cut,
          ),
        };
      }),
    );
  }

  /**
   * Write one corner's radius into the outline's `cornerRounding`, snapshotting
   * the current geometry as `sharpPathData` the first time. The commit pipeline's
   * `deriveRoundedLeatherCorners` turns the metadata into the visible outline,
   * which is what keeps the radius adjustable afterwards.
   *
   * A radius of 0 removes the entry, and emptying the list drops the whole block
   * so the outline returns to a pristine un-rounded state.
   */
  private commitRadius(
    ref: OutlineRef,
    pathIndex: number,
    segmentIndex: number,
    radius: number,
  ): void {
    const label =
      radius < ROUND_MIN_RADIUS_MM ? "Sharpen corner" : "Round corner";
    this.host.commit(label, (proj) =>
      mapPiece(proj, ref.pieceId, (piece) => {
        if (ref.kind === "piece") {
          const prev = piece.cornerRounding;
          const corners = mergeCornerRadius(
            prev?.corners ?? [],
            pathIndex,
            segmentIndex,
            radius,
          );
          const sharp = prev?.sharpPathData ?? piece.shapePathData;
          if (corners.length === 0) {
            // Last rounding removed: restore the SHARP geometry as well as
            // dropping the block. Without the restore the derive pass sees no
            // rounding, leaves the still-rounded outline alone, and the fillet
            // becomes permanent — the corner would read as sharpened while the
            // shape stayed round.
            const { cornerRounding: _drop, ...rest } = piece;
            return { ...rest, shapePathData: sharp };
          }
          return {
            ...piece,
            cornerRounding: { sharpPathData: sharp, corners },
          };
        }
        return {
          ...piece,
          cutouts: (piece.cutouts ?? []).map((cut) => {
            if (cut.id !== ref.cutoutId) return cut;
            const prev = cut.cornerRounding;
            const corners = mergeCornerRadius(
              prev?.corners ?? [],
              pathIndex,
              segmentIndex,
              radius,
            );
            const sharp = prev?.sharpPathData ?? cut.pathData;
            if (corners.length === 0) {
              // Same restore as the part outline above.
              const { cornerRounding: _drop, ...rest } = cut;
              return { ...rest, pathData: sharp };
            }
            return {
              ...cut,
              cornerRounding: { sharpPathData: sharp, corners },
            };
          }),
        };
      }),
    );
  }
}
